import { Check, History, Loader2, RotateCcw, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { Timesheet } from '@/types/timesheet';

export interface TimesheetReviewFooterProps {
  timesheet: Timesheet | null;
  currentEmployeeId: number;
  isEditMode: boolean;
  isSavingEdits: boolean;
  hasUnsavedChanges: boolean;
  isViewingApproving: boolean;
  isViewingRejecting: boolean;
  isViewingProcessing: boolean;
  canApprove: () => boolean;
  canReject: () => boolean;
  onClose: () => void;
  onShowHistory: () => void;
  onCancelEdit: () => void;
  onSaveEdits: () => void;
  onApprove: () => void;
  onReject: () => void;
}

export function TimesheetReviewFooter({
  timesheet,
  currentEmployeeId,
  isEditMode,
  isSavingEdits,
  hasUnsavedChanges,
  isViewingApproving,
  isViewingRejecting,
  isViewingProcessing,
  canApprove,
  canReject,
  onClose,
  onShowHistory,
  onCancelEdit,
  onSaveEdits,
  onApprove,
  onReject,
}: TimesheetReviewFooterProps) {
  const isOwnTimesheet = timesheet?.cannotApproveOwnTimesheet === true && timesheet?.employeeId === currentEmployeeId;

  return (
    <DialogFooter className="flex-col sm:flex-row gap-2 flex-shrink-0 border-t pt-4">
      <div className="flex gap-2 flex-1">
        <Button
          variant="outline"
          onClick={onClose}
        >
          Close
        </Button>
        {timesheet?.id && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={onShowHistory}
                  aria-label="View timesheet history"
                >
                  <History className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">View History</span>
                  <span className="sm:hidden">History</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View complete audit trail and change history</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {/* Edit mode actions */}
      {isEditMode ? (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onCancelEdit}
            disabled={isSavingEdits}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={onSaveEdits}
            disabled={isSavingEdits || !hasUnsavedChanges}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSavingEdits ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={onApprove}
                  disabled={!canApprove() || isOwnTimesheet || isViewingProcessing}
                >
                  {isViewingApproving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  {isViewingApproving ? 'Approving…' : 'Approve'}
                </Button>
              </span>
            </TooltipTrigger>
            {!canApprove() && (
              <TooltipContent><p>You don't have permission to approve timesheets</p></TooltipContent>
            )}
            {isOwnTimesheet && (
              <TooltipContent><p>Cannot approve own timesheet</p></TooltipContent>
            )}
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="destructive"
                  onClick={onReject}
                  disabled={!canReject() || isOwnTimesheet || isViewingProcessing}
                >
                  {isViewingRejecting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <X className="h-4 w-4 mr-2" />
                  )}
                  {isViewingRejecting ? 'Rejecting…' : 'Reject'}
                </Button>
              </span>
            </TooltipTrigger>
            {!canReject() && (
              <TooltipContent><p>You don't have permission to reject timesheets</p></TooltipContent>
            )}
            {isOwnTimesheet && (
              <TooltipContent><p>Cannot reject own timesheet</p></TooltipContent>
            )}
          </Tooltip>
        </div>
      )}
    </DialogFooter>
  );
}
